import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError, exportCsv } from "@/lib/api";
import { inr, num, MONTHS } from "@/lib/format";
import { PageLoader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { TrendingUp, Upload, Download, FileSpreadsheet, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const YEARS = Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - 5 + i);

export default function SalesManager() {
  const qc = useQueryClient();
  const { data: books } = useQuery({ queryKey: ["books"], queryFn: async () => (await api.get("/admin/books")).data });
  const { data: sales, isLoading } = useQuery({ queryKey: ["admin-sales"], queryFn: async () => (await api.get("/admin/sales")).data });

  const [entry, setEntry] = useState({ book_id: "", platform: "", year: String(new Date().getFullYear()), month: String(new Date().getMonth() + 1), copies_sold: "" });
  const selectedBook = books?.find((b) => b.id === entry.book_id);
  const [uploadResult, setUploadResult] = useState(null);
  const fileRef = useRef();

  const addSale = useMutation({
    mutationFn: async () => (await api.post("/admin/sales", {
      book_id: entry.book_id, platform: entry.platform,
      year: parseInt(entry.year), month: parseInt(entry.month), copies_sold: parseInt(entry.copies_sold),
    })).data,
    onSuccess: () => {
      toast.success("Sales updated & profit recalculated");
      setEntry({ ...entry, copies_sold: "" });
      qc.invalidateQueries({ queryKey: ["admin-sales"] });
      qc.invalidateQueries({ queryKey: ["admin-overview"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const upload = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post("/admin/sales/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (data) => {
      setUploadResult(data);
      toast.success(`${data.processed} of ${data.total_rows} rows imported`);
      qc.invalidateQueries({ queryKey: ["admin-sales"] });
      qc.invalidateQueries({ queryKey: ["admin-overview"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/sales/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sales_template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download template");
    }
  };

  const validEntry = entry.book_id && entry.platform && entry.copies_sold;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Sales management</p>
          <p className="text-sm text-muted-foreground mt-1">Update copies sold per platform — profit and available balance recalculate automatically.</p>
        </div>
        <Button variant="outline" className="gap-2" data-testid="export-sales" onClick={() => exportCsv("/admin/sales/export", "sales.csv").catch(() => toast.error("Export failed"))}>
          <Download className="h-4 w-4" /> Export
        </Button>
      </div>

      <Tabs defaultValue="single">
        <TabsList>
          <TabsTrigger value="single" data-testid="tab-single">Single entry</TabsTrigger>
          <TabsTrigger value="bulk" data-testid="tab-bulk">Bulk upload (CSV / XLSX)</TabsTrigger>
        </TabsList>

        <TabsContent value="single" className="mt-4">
          <Card className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
              <div className="space-y-2 lg:col-span-2">
                <Label>Book</Label>
                <Select value={entry.book_id} onValueChange={(v) => setEntry({ ...entry, book_id: v, platform: "" })}>
                  <SelectTrigger data-testid="sale-book-select"><SelectValue placeholder="Select book" /></SelectTrigger>
                  <SelectContent>
                    {books?.map((b) => <SelectItem key={b.id} value={b.id}>{b.title} — {b.author_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Platform</Label>
                <Select value={entry.platform} onValueChange={(v) => setEntry({ ...entry, platform: v })} disabled={!selectedBook}>
                  <SelectTrigger data-testid="sale-platform-select"><SelectValue placeholder="Platform" /></SelectTrigger>
                  <SelectContent>
                    {selectedBook?.platforms.map((p) => <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Month</Label>
                <Select value={entry.month} onValueChange={(v) => setEntry({ ...entry, month: v })}>
                  <SelectTrigger data-testid="sale-month-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.slice(1).map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Year</Label>
                <Select value={entry.year} onValueChange={(v) => setEntry({ ...entry, year: v })}>
                  <SelectTrigger data-testid="sale-year-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Copies sold</Label>
                <Input data-testid="sale-copies-input" type="number" min="0" value={entry.copies_sold} onChange={(e) => setEntry({ ...entry, copies_sold: e.target.value })} placeholder="0" />
              </div>
            </div>
            <div className="flex items-center justify-between mt-5">
              <p className="text-xs text-muted-foreground">
                {selectedBook && entry.platform
                  ? `Royalty: ${inr(selectedBook.platforms.find((p) => p.name === entry.platform)?.royalty_per_copy)} / copy`
                  : "Select a book and platform to see the royalty rate."}
              </p>
              <Button data-testid="submit-sale-button" onClick={() => addSale.mutate()} disabled={!validEntry || addSale.isPending} className="gap-2">
                {addSale.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />}
                Update sales
              </Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="bulk" className="mt-4">
          <Card className="p-6 space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="font-head font-medium">Bulk update via file</h3>
                <p className="text-sm text-muted-foreground mt-0.5">One row per book &amp; month, with a column for each platform (Amazon, Flipkart, Kindle…). Enter copies sold — leave 0 where there were no sales.</p>
              </div>
              <Button variant="outline" className="gap-2" data-testid="download-template-button" onClick={downloadTemplate}>
                <Download className="h-4 w-4" /> Download template
              </Button>
            </div>

            <label
              htmlFor="bulk-file"
              className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-xl py-12 cursor-pointer hover:bg-muted/40 transition-colors"
            >
              {upload.isPending ? (
                <Loader2 className="h-8 w-8 text-primary animate-spin" />
              ) : (
                <FileSpreadsheet className="h-8 w-8 text-muted-foreground" strokeWidth={1.5} />
              )}
              <p className="text-sm font-medium">Click to choose a .csv or .xlsx file</p>
              <p className="text-xs text-muted-foreground">Updates multiple authors and platforms at once</p>
              <input
                ref={fileRef}
                id="bulk-file"
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                data-testid="bulk-file-input"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    setUploadResult(null);
                    upload.mutate(f);
                  }
                  e.target.value = "";
                }}
              />
            </label>

            {uploadResult && (
              <div data-testid="upload-result" className="rounded-lg border border-border p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium">{uploadResult.processed}</span> of {uploadResult.total_rows} rows imported successfully.
                </div>
                {uploadResult.errors?.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-destructive"><AlertCircle className="h-4 w-4" /> {uploadResult.errors.length} row(s) had issues:</div>
                    <ul className="text-xs text-muted-foreground list-disc pl-6 max-h-40 overflow-y-auto">
                      {uploadResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="font-head font-medium">Recorded sales</h3>
        </div>
        {isLoading ? (
          <PageLoader />
        ) : sales.length === 0 ? (
          <EmptyState icon={TrendingUp} title="No sales recorded yet" desc="Add a single entry or upload a file to get started." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead>Book</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Copies</TableHead>
                  <TableHead className="text-right">Profit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sales.map((s) => (
                  <TableRow key={s.id} data-testid={`sale-row-${s.id}`}>
                    <TableCell className="font-medium">{s.book_title}</TableCell>
                    <TableCell>{s.platform}</TableCell>
                    <TableCell className="text-muted-foreground">{MONTHS[s.month]} {s.year}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(s.copies_sold)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium text-primary">{inr(s.profit)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
